import type { CompleteEvent, EditorEvent, TunaEditor } from '@tunasong/plugin-lib'
import { Editor, Transforms } from '@tunasong/plugin-lib'
import { ReactEditor } from 'slate-react'
import { useCurrentUser } from '@tunasong/redux'
import type { Comment, CoreElement } from '@tunasong/schemas'
import { UserAvatar } from '@tunasong/ui-lib'
import type { FC, HTMLAttributes } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { ChatEditor } from '../editor/chat-editor.js'
import styles from './chat-box.module.css'

export interface ChatBoxProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  onChange: (comment: Comment) => void
  onComplete: (event: EditorEvent) => void
  size?: 'small' | 'medium' | 'large'
  /**
   * One-shot draft text to insert at the cursor. When this changes to a
   * non-empty value the ChatBox clears any current content, inserts the text,
   * focuses the input, and calls `onDraftConsumed` so the parent can clear
   * its state. Designed for empty-state chips that pre-fill rather than
   * auto-submit.
   */
  pendingDraft?: string
  onDraftConsumed?: () => void
  /**
   * Stable id forwarded to the underlying editor's root element. Lets siblings
   * (like an empty-state header) wire `aria-describedby` to this ChatBox so
   * screen readers hear the surrounding context.
   */
  id?: string
}

export const ChatBox: FC<ChatBoxProps> = props => {
  const {
    onChange,
    onComplete,
    size = 'tiny',
    pendingDraft,
    onDraftConsumed,
    id,
    ...restProps
  } = props

  const { userId = 'unknown' } = useCurrentUser()
  const chatElement = useMemo(
    () =>
      ({
        type: 'comment',
        userId,
        children: [{ type: 'line', children: [{ text: '' }] }],
      }) satisfies Partial<CoreElement<Comment>>,
    [userId]
  )

  const editorRef = useRef<TunaEditor | null>(null)
  const handleEditor = useCallback((editor: TunaEditor) => {
    editorRef.current = editor
  }, [])

  /**
   * When `pendingDraft` flips to a non-empty value, replace the editor body
   * with the prompt text and focus the input. Effect is keyed on the draft
   * value, so calling it twice with the same string is a no-op — the parent
   * is responsible for changing the value to a new string (or re-setting it
   * after clearing) to retrigger.
   */
  useEffect(() => {
    const editor = editorRef.current
    if (!editor || !pendingDraft || pendingDraft.length === 0) {
      return
    }
    Editor.withoutNormalizing(editor, () => {
      const at = {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      }
      Transforms.delete(editor, { at })
      Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
      Transforms.insertText(editor, pendingDraft)
    })
    try {
      ReactEditor.focus(editor as unknown as ReactEditor)
    } catch {
      // Focus can throw if the editor is unmounted mid-insert; silent recovery
      // is the right call — the user can still click to focus.
    }
    onDraftConsumed?.()
  }, [pendingDraft, onDraftConsumed])

  const handleComplete = useCallback(
    (event: EditorEvent) => {
      const { editor } = event.detail as CompleteEvent

      /** Clear the editor */
      Editor.withoutNormalizing(editor, () => {
        const at = {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        }
        Transforms.delete(editor, { at })
        Transforms.setNodes(editor, { type: 'line', children: [{ text: '' }] })
      })

      onComplete(event)
    },
    [onComplete]
  )
  const fontSize = size === 'medium' ? 16 : size === 'small' ? 14 : 18

  return (
    <div
      className={styles.container}
      id={id}
      {...restProps}
    >
      <UserAvatar size={size} className={styles.avatar} />
      <ChatEditor
        className={styles.editor}
        style={{ fontSize }}
        element={chatElement}
        autoFocus={true}
        onChange={onChange}
        onComplete={handleComplete}
        onEditor={handleEditor}
      />
    </div>
  )
}
